import { useState, useEffect, useCallback } from 'react';
import WattelzceFilterSidebar, { type WattelzceFilterValues } from '../components/WattelzceFilterSidebar';
import KpiCard from '../components/KpiCard';
import EnergyByMonthChart from '../components/charts/EnergyByMonthChart';
import TopDevicesEnergyChart from '../components/charts/TopDevicesEnergyChart';
import RevenueByDeviceChart from '../components/charts/RevenueByDeviceChart';
import EnergyByVehicleChart from '../components/charts/EnergyByVehicleChart';
import {
  wattelzceApi,
  type KpiData,
  type EnergyByMonth,
  type TopDeviceEnergy,
  type RevenueByDevice,
  type EnergyByVehicle,
  type DeviceNameOption,
  type DeviceVendorOption,
} from '../api/wattelzce';
import { useI18n, type TranslationKey } from '../i18n';

// Custom icons for Wattelzce
const WattelzceIcons = {
  energy: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  cost: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  plug: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  time: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

function WattelzcePage() {
  const { t, locale } = useI18n();
  // Filter options state
  const [deviceNameOptions, setDeviceNameOptions] = useState<DeviceNameOption[]>([]);
  const [deviceVendorOptions, setDeviceVendorOptions] = useState<DeviceVendorOption[]>([]);
  
  // Data state
  const [kpis, setKpis] = useState<KpiData | null>(null);
  const [energyByMonth, setEnergyByMonth] = useState<EnergyByMonth[]>([]);
  const [topDevicesEnergy, setTopDevicesEnergy] = useState<TopDeviceEnergy[]>([]);
  const [revenueByDevice, setRevenueByDevice] = useState<RevenueByDevice[]>([]);
  const [energyByVehicle, setEnergyByVehicle] = useState<EnergyByVehicle[]>([]);
  
  // Loading states
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [loadingEnergyMonth, setLoadingEnergyMonth] = useState(true);
  const [loadingTopDevices, setLoadingTopDevices] = useState(true);
  const [loadingRevenue, setLoadingRevenue] = useState(true);
  const [loadingVehicle, setLoadingVehicle] = useState(true);
  
  // Error state
  const [error, setError] = useState<TranslationKey | null>(null);

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [namesRes, vendorsRes] = await Promise.all([
          wattelzceApi.getDeviceNames(),
          wattelzceApi.getDeviceVendors(),
        ]);
        setDeviceNameOptions(namesRes.data);
        setDeviceVendorOptions(vendorsRes.data);
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  // Fetch all data based on filters
  const fetchData = useCallback(async (filters: WattelzceFilterValues) => {
    const apiFilters = {
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      deviceName: filters.deviceName || undefined,
      deviceVendor: filters.deviceVendor || undefined,
    };

    setError(null);

    // Fetch KPIs
    setLoadingKpis(true);
    try {
      const kpiResponse = await wattelzceApi.getKpis(apiFilters);
      setKpis(kpiResponse.data);
    } catch (err) {
      console.error('Failed to fetch KPIs:', err);
      setError('leg2_kpi_load_error');
    } finally {
      setLoadingKpis(false);
    }

    // Fetch Energy by Month
    setLoadingEnergyMonth(true);
    try {
      const energyMonthResponse = await wattelzceApi.getEnergyByMonth(apiFilters);
      setEnergyByMonth(energyMonthResponse.data);
    } catch (err) {
      console.error('Failed to fetch energy by month:', err);
    } finally {
      setLoadingEnergyMonth(false);
    }

    // Fetch Top Devices by Energy
    setLoadingTopDevices(true);
    try {
      const topDevicesResponse = await wattelzceApi.getTopDevicesEnergy(apiFilters);
      setTopDevicesEnergy(topDevicesResponse.data);
    } catch (err) {
      console.error('Failed to fetch top devices:', err);
    } finally {
      setLoadingTopDevices(false);
    }

    // Fetch Revenue by Device
    setLoadingRevenue(true);
    try {
      const revenueResponse = await wattelzceApi.getRevenueByDevice(apiFilters);
      setRevenueByDevice(revenueResponse.data);
    } catch (err) {
      console.error('Failed to fetch revenue:', err);
    } finally {
      setLoadingRevenue(false);
    }

    // Fetch Energy by Vehicle
    setLoadingVehicle(true);
    try {
      const vehicleResponse = await wattelzceApi.getEnergyByVehicle(apiFilters);
      setEnergyByVehicle(vehicleResponse.data);
    } catch (err) {
      console.error('Failed to fetch energy by vehicle:', err);
    } finally {
      setLoadingVehicle(false);
    }
  }, []);

  const handleFilterChange = useCallback((filters: WattelzceFilterValues) => {
    fetchData(filters);
  }, [fetchData]);

  return (
    <div className="flex min-h-screen -m-8">
      {/* Filter Sidebar */}
      <WattelzceFilterSidebar
        onFilterChange={handleFilterChange}
        deviceNameOptions={deviceNameOptions}
        deviceVendorOptions={deviceVendorOptions}
      />

      {/* Main Content */}
      <main className="flex-1 p-6 bg-gray-50">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <img src="/logos/wattelzce-logo.png" alt="Wattelzce" className="h-12 w-auto" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('leg2_wattelzce_title')}</h1>
            <p className="text-gray-600 mt-1">{t('leg2_wattelzce_subtitle')}</p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{t(error)}</p>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard
            title={t('chg_kpi_total_energy')}
            value={loadingKpis ? '...' : `${(kpis?.totalEnergyKwh || 0).toLocaleString(locale, {maximumFractionDigits: 1})} kWh`}
            icon={WattelzceIcons.energy}
            color="green"
            subtitle={t('leg2_cumulative_consumption')}
          />
          <KpiCard
            title={t('leg2_revenue')}
            value={loadingKpis ? '...' : `${(kpis?.totalCost || 0).toLocaleString(locale, {maximumFractionDigits: 2})} €`}
            icon={WattelzceIcons.cost}
            color="blue"
            subtitle={t('leg2_at_rate_per_kwh')}
          />
          <KpiCard
            title={t('leg2_total_chargers')}
            value={loadingKpis ? '...' : kpis?.totalDevices || 0}
            icon={WattelzceIcons.plug}
            color="purple"
            subtitle={t('leg2_active_chargers', { n: kpis?.activeDevices || 0 })}
          />
          <KpiCard
            title={t('leg2_transactions')}
            value={loadingKpis ? '...' : kpis?.totalTransactions || 0}
            icon={WattelzceIcons.time}
            color="yellow"
            subtitle={t('leg2_charging_sessions')}
          />
        </div>

        {/* Charts - 2 per row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Energy by Month Line Chart */}
          <EnergyByMonthChart data={energyByMonth} loading={loadingEnergyMonth} />
          
          {/* Top Devices Energy Funnel Chart */}
          <TopDevicesEnergyChart data={topDevicesEnergy} loading={loadingTopDevices} />
          
          {/* Revenue by Device Horizontal Bar Chart */}
          <RevenueByDeviceChart data={revenueByDevice} loading={loadingRevenue} />
          
          {/* Energy by Vehicle Pie Chart */}
          <EnergyByVehicleChart data={energyByVehicle} loading={loadingVehicle} />
        </div>
      </main>
    </div>
  );
}

export default WattelzcePage;
